import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FaBell } from "react-icons/fa";
import "./NotificationBell.css";
import {
  setNotifications,
  markAllRead,
  markOneRead,
} from "../../../redux/slices/notificationSlice";
import {
  fetchNotificationsAPI,
  markNotificationsReadAPI,
  updateNotificationStatusAPI,
} from "../../../operations/services/getNotifications";

const NotificationBell = ({ onNotificationClick }) => {
  const [open, setOpen] = useState(false);
  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const panelRef = useRef(null);
  const dispatch = useDispatch();

  const userId = useSelector(
    (state) =>
      state.login?.login?.data?.data?.[0]?.UserId ||
      state.login?.login?.data?.data?.[0]?.AdminID ||
      state.login?.login?.data?.data?.[0]?.AgentID,
  );
  const { list } = useSelector((state) => state.notifications);

  const unreadCount = list.filter((n) => !n.IsSeen).length;

  useEffect(() => {
    if (!userId) return;
    (async () => {
      const data = await fetchNotificationsAPI(userId);
      if (Array.isArray(data)) {
        console.log("Fetched notifications:", data);

        dispatch(setNotifications(data));
      }
    })();
  }, [userId, dispatch]);

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (open && panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleBellClick = async () => {
    const willOpen = !open;
    setOpen(willOpen);

    if (willOpen) {
      const data = await fetchNotificationsAPI(userId);
      if (Array.isArray(data)) {
        dispatch(setNotifications(data));
      }
    }
  };

  const handleMarkAllRead = async () => {
    if (!userId || isMarkingRead) {
      console.warn(
        "handleMarkAllRead: No userId available or already processing",
      );
      return;
    }

    try {
      setIsMarkingRead(true);

      dispatch(markAllRead());

      const success = await markNotificationsReadAPI(userId);

      if (success) {
        console.log("All notifications marked as read successfully");
      } else {
        console.warn(
          "Failed to mark notifications as read. Refetching from server...",
        );
        const refreshedData = await fetchNotificationsAPI(userId);
        if (Array.isArray(refreshedData)) {
          dispatch(setNotifications(refreshedData));
        }
      }
    } catch (err) {
      console.error("Error in handleMarkAllRead:", err);
      const refreshedData = await fetchNotificationsAPI(userId);
      if (Array.isArray(refreshedData)) {
        dispatch(setNotifications(refreshedData));
      }
    } finally {
      setIsMarkingRead(false);
    }
  };

  const handleItemClick = async (notification) => {
    if (!notification.IsSeen) {
      try {
        dispatch(
          markOneRead({
            notificationId: notification.NotificationId,
            isSeen: true,
          }),
        );

        await updateNotificationStatusAPI(
          notification.NotificationId,
          userId,
          true,
        );
        console.log(
          `Notification ${notification.NotificationId} marked as read`,
        );
      } catch (err) {
        console.error("Error marking notification as read:", err);
        const refreshedData = await fetchNotificationsAPI(userId);
        if (Array.isArray(refreshedData)) {
          dispatch(setNotifications(refreshedData));
        }
      }
    }

    setOpen(false);
    if (onNotificationClick) onNotificationClick(notification);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  return (
    <div className="notif-wrapper" ref={panelRef}>
      {/* ─── Bell Button ─── */}
      <button
        className="notif-bell-btn"
        onClick={handleBellClick}
        aria-label="Notifications"
        id="notif-bell-button"
      >
        <FaBell className="notif-bell-icon" />
        {unreadCount > 0 && (
          <span className="notif-badge" id="notif-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* ─── Dropdown Panel ─── */}
      {open && (
        <div className="notif-panel" id="notif-panel">
          <div className="notif-panel-header">
            <span className="notif-panel-title">Notifications</span>
            {unreadCount > 0 && (
              <button
                className="notif-mark-read-btn"
                onClick={handleMarkAllRead}
                disabled={isMarkingRead}
              >
                {isMarkingRead ? "Marking..." : "Mark all as read"}
              </button>
            )}
          </div>

          <div className="notif-list">
            {list.length === 0 ? (
              <div className="notif-empty">You're all caught up! 🎉</div>
            ) : (
              list.map((n, idx) => (
                <div
                  key={n.NotificationId ?? idx}
                  className={`notif-item ${n.IsSeen ? "notif-read" : "notif-unread"}`}
                  onClick={() => handleItemClick(n)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="notif-item-dot" />
                  <div className="notif-item-body">
                    <p className="notif-item-text">
                      Hey, you have a message from{" "}
                      <strong>
                        {(() => {
                          const cleaned =
                            typeof n.SenderName === "string"
                              ? n.SenderName.replace(/\bnull\b/gi, "").trim()
                              : "";

                          return cleaned
                            ? cleaned
                            : n.SenderUserId
                              ? `User #${n.SenderUserId}`
                              : "someone";
                        })()}
                      </strong>
                      . It says:{" "}
                      <em>
                        &ldquo;
                        {n.Message?.length > 120
                          ? n.Message.slice(0, 120) + "…"
                          : n.Message}
                        &rdquo;
                      </em>
                    </p>
                    {n.CreatedAt && (
                      <span className="notif-item-time">
                        {formatTime(n.CreatedAt)}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
